import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { sportCategories, featuredEvents } from '@/mocks/homeData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'football');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const currentCategory = useMemo(
    () => sportCategories.find((c) => c.id === activeCategory),
    [activeCategory]
  );

  const filteredEvents = useMemo(
    () => featuredEvents.filter((e) => {
      if (activeCategory === 'football') return e.category === '足球';
      if (activeCategory === 'basketball') return e.category === '篮球';
      if (activeCategory === 'tennis') return e.category === '网球';
      if (activeCategory === 'esports') return e.category === '电竞';
      if (activeCategory === 'table-tennis') return e.category === '乒乓球';
      if (activeCategory === 'baseball') return e.category === '棒球';
      return false;
    }),
    [activeCategory]
  );

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: '赛事分类', path: '' },
    { label: currentCategory?.name || '', path: '' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-950 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-1.5 text-xs mb-4" aria-label="面包屑导航">
              {breadcrumbs.map((crumb, idx) => (
                <span key={crumb.label} className="flex items-center gap-1.5">
                  {idx > 0 && (
                    <span className="text-background-500">/</span>
                  )}
                  {crumb.path ? (
                    <Link to={crumb.path} className="text-background-400 hover:text-background-200 transition-colors duration-200 whitespace-nowrap">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-background-200 whitespace-nowrap">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
              {currentCategory?.name}赛事
            </h1>
            <p className="text-sm text-background-400 mt-2 max-w-[600px]">
              {currentCategory?.description}
            </p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto py-3 flex-wrap">
              {sportCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${cat.icon} text-[10px]`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
            {filteredEvents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px] text-sm">
                  <thead>
                    <tr className="border-b border-background-200/60">
                      <th className="text-left py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">联赛</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">时间</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">主队</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">主胜赔率</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">平局</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">客队</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">客胜赔率</th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-foreground-500 whitespace-nowrap">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((event) => (
                      <tr key={event.id} className="border-b border-background-100/80 hover:bg-background-100/50 transition-colors duration-150">
                        <td className="py-3 px-2">
                          <span className="text-xs text-foreground-600 whitespace-nowrap">{event.league}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className="text-xs text-foreground-600 whitespace-nowrap">{event.time}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className="text-xs font-medium text-foreground-800 whitespace-nowrap">{event.homeTeam}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-700 rounded whitespace-nowrap">{event.homeOdds}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          {event.drawOdds > 0 ? (
                            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-background-100 text-foreground-600 rounded whitespace-nowrap">{event.drawOdds}</span>
                          ) : (
                            <span className="text-xs text-foreground-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className="text-xs font-medium text-foreground-800 whitespace-nowrap">{event.awayTeam}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-background-100 text-foreground-700 rounded whitespace-nowrap">{event.awayOdds}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded whitespace-nowrap ${
                            event.status === 'live'
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-secondary-100 text-secondary-700'
                          }`}>
                            {event.status === 'live' && (
                              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse inline-block"></span>
                            )}
                            {event.status === 'live' ? '进行中' : '即将开始'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">该分类暂无赛事数据</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunsport.com/' },
              { '@type': 'ListItem', position: 2, name: '赛事分类', item: 'https://www.kaiyunsport.com/category/football' },
              { '@type': 'ListItem', position: 3, name: `${currentCategory?.name}赛事` },
            ],
          }),
        }}
      />
    </>
  );
}